import { useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { classNames } from "@/lib/format";
import { useInvalidateMyFollowUps } from "@/hooks/useMyFollowUps";
import { UserCheck, CalendarClock, CheckCircle2, RotateCcw, AlertTriangle, Circle } from "lucide-react";

const dayDiff = (due) => Math.round((new Date(`${due}T00:00:00`) - new Date(new Date().toDateString())) / 86400000);

export function followUpLabel(fu, state) {
  if (!fu || state === "none") return null;
  if (state === "done") return `Done${fu.done_by_name ? ` by ${fu.done_by_name}` : ""}`;
  if (!fu.due_date) return "Open";
  const n = dayDiff(fu.due_date);
  if (n < 0) return `Overdue ${-n}d`;
  if (n === 0) return "Due today";
  return `Due in ${n}d`;
}

const STYLE = {
  done: "bg-[hsl(var(--emerald))]/15 text-[hsl(var(--emerald))]",
  overdue: "bg-[hsl(var(--rose))]/15 text-[hsl(var(--rose))]",
  due_soon: "bg-[hsl(var(--amber))]/15 text-[hsl(var(--amber))]",
  open: "bg-muted text-foreground",
};
const ICON = { done: CheckCircle2, overdue: AlertTriangle, due_soon: CalendarClock, open: Circle };

export function FollowUpChip({ fu, state, testid }) {
  const label = followUpLabel(fu, state);
  if (!label) return null;
  const Icon = ICON[state] || Circle;
  return (
    <span className={classNames("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold", STYLE[state] || STYLE.open)} data-testid={testid} title={fu.due_date ? `Due ${fu.due_date}` : undefined}>
      <Icon className="h-3 w-3" />{label}{fu.owner_name && state !== "done" ? <span className="font-medium opacity-80">· {fu.owner_name}</span> : null}
    </span>
  );
}

// OP-036: write rights come from the role (canEdit), never from the assignment: an assigned viewer stays read-only.
// "Clear" asks for confirmation (owner, due date and state are erased for good); editing a done follow-up warns
// that saving reopens it (current server behaviour, kept).
export default function FollowUpControls({ videoId, comment, members = [], canEdit, onChange, size = "sm", currentUserId }) {
  const fu = comment.follow_up;
  const state = comment.follow_up_state || "none";
  const [open, setOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [owner, setOwner] = useState(fu?.owner_id || "none");
  const [due, setDue] = useState(fu?.due_date || "");
  const [busy, setBusy] = useState(false);
  const invalidateFollowUps = useInvalidateMyFollowUps();
  const base = `/videos/${videoId}/comments/${comment.id}/follow-up`;

  const run = async (fn, msg) => {
    if (!canEdit || busy) return;
    setBusy(true);
    try { const { data } = await fn(); onChange?.(data); invalidateFollowUps(); if (msg) toast.success(msg); setOpen(false); }
    catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const save = () => run(() => api.patch(base, { owner_id: owner === "none" ? null : owner, due_date: due || null }), owner === "none" && !due ? "Follow-up cleared" : "Follow-up assigned");
  const clear = () => { setConfirmClear(false); setOwner("none"); setDue(""); run(() => api.patch(base, { owner_id: null, due_date: null }), "Follow-up cleared"); };
  const done = () => run(() => api.post(`${base}/complete`), "Marked done");
  const reopen = () => run(() => api.post(`${base}/reopen`), "Reopened");

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={`follow-up-${comment.id}`}>
      <FollowUpChip fu={fu} state={state} testid={`follow-up-chip-${comment.id}`} />
      {!canEdit && fu && currentUserId && fu.owner_id === currentUserId && state !== "done" && (
        <span className="text-[11px] text-muted-foreground" data-testid={`follow-up-readonly-${comment.id}`}>Assigné à vous, en lecture seule : un éditeur ou le propriétaire peut le clôturer.</span>
      )}
      {canEdit && (
        <>
          <Popover open={canEdit && open} onOpenChange={(o) => { setOpen(o); setConfirmClear(false); if (o) { setOwner(fu?.owner_id || "none"); setDue(fu?.due_date || ""); } }}>
            <PopoverTrigger asChild>
              <Button size={size} variant="ghost" className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground" data-testid={`follow-up-assign-${comment.id}`}>
                <UserCheck className="h-3.5 w-3.5 mr-1" />{fu ? "Edit follow-up" : "Assign"}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 space-y-3" data-testid={`follow-up-popover-${comment.id}`}>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Follow-up</div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Owner</label>
                <Select value={owner} onValueChange={setOwner}>
                  <SelectTrigger className="h-9" data-testid={`follow-up-owner-select-${comment.id}`}><SelectValue placeholder="Pick a teammate" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No owner</SelectItem>
                    {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}{m.role ? <span className="text-muted-foreground"> · {m.role}</span> : null}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Due date <span className="opacity-60">(optional)</span></label>
                <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="h-9" data-testid={`follow-up-due-input-${comment.id}`} />
              </div>
              {fu?.status === "done" && (
                <p className="text-xs text-[hsl(var(--amber))]" role="note" data-testid={`follow-up-reopen-warning-${comment.id}`}>Ce suivi est terminé : enregistrer un responsable ou une échéance le rouvrira (état « ouvert »).</p>
              )}
              {confirmClear ? (
                <div role="alert" className="space-y-2 rounded-md border border-border p-2 text-xs" data-testid={`follow-up-clear-dialog-${comment.id}`}>
                  <p>Le responsable ({fu?.owner_name || "aucun"}), l’échéance ({fu?.due_date || "aucune"}) et l’état ({fu?.status === "done" ? "terminé" : "ouvert"}) de ce suivi seront effacés, sans restauration possible. Seul l’événement d’activité restera.</p>
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setConfirmClear(false)} disabled={busy} data-testid={`follow-up-clear-cancel-${comment.id}`}>Annuler</Button>
                    <Button size="sm" variant="destructive" onClick={clear} disabled={busy} data-testid={`follow-up-clear-confirm-${comment.id}`}>Effacer le suivi</Button>
                  </div>
                </div>
              ) : (
              <div className="flex items-center justify-between gap-2 pt-1">
                {fu ? <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => setConfirmClear(true)} disabled={busy} data-testid={`follow-up-clear-${comment.id}`}>Clear</Button> : <span />}
                <Button size="sm" onClick={save} disabled={busy || (owner === "none" && !due)} data-testid={`follow-up-save-${comment.id}`}>{busy ? "Saving…" : "Save"}</Button>
              </div>
              )}
            </PopoverContent>
          </Popover>
          {fu && state !== "done" && (
            <Button size={size} variant="ghost" className="h-7 px-2 text-xs text-[hsl(var(--emerald))] hover:text-[hsl(var(--emerald))]" onClick={done} disabled={busy} data-testid={`follow-up-done-${comment.id}`}>
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark done
            </Button>
          )}
          {fu && state === "done" && (
            <Button size={size} variant="ghost" className="h-7 px-2 text-xs text-muted-foreground" onClick={reopen} disabled={busy} data-testid={`follow-up-reopen-${comment.id}`}>
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reopen
            </Button>
          )}
        </>
      )}
    </div>
  );
}
